import { css } from "@emotion/react"
import {
  Fragment,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react"
import { array, arrayOf, bool, func, object, shape, string } from "prop-types"
import { buildSubdomain } from "@planningcenter/cc-url"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { Icon, DateSelector } from "source/shared/components"
import { ServerRenderedProps } from "source/shared/contexts/ServerRenderedProps"
import { Heading } from "@planningcenter/doxy-web"
import { GenderSelect } from "./GenderSelect"
import { HouseholdRoleSection } from "./HouseholdRoleSection"
import { CampusSelect } from "./FormElements/CampusSelect"
import { MembershipSelect } from "./FormElements/MembershipSelect"
import { colors } from "source/shared/colors"
import { WebBootContext } from "source/publishing/WebBoot"
import { Address } from "./FormElements/Address"
import { GradeAndSchool } from "./FormElements/GradeAndSchool"
import { LocationSelect } from "./FormElements/LocationSelect"
import { CustomFieldsForm } from "./CustomFieldsForm"
import { useSearchParams } from "source/myChurchCenter/hooks/useSearchParams"
import { useSession } from "source/shared/hooks/useSession"
import { useBuiltinFieldRules } from "source/shared/hooks/useBuiltinFieldRules"
import { groupCustomFieldRecordsForSaving } from "source/myChurchCenter/utils/groupCustomFieldRecords"
import { uploadFileAttachment } from "source/people/components/utils"
import { groupCustomFieldsByHeaders } from "./groupCustomFieldsByHeader"
import { useFlipperFeatureEnabled } from "source/shared/flipperFeatures"

const NEW_RECORD_PREFIX = "new_record_"

function isNewRecord(record) {
  return record.id?.startsWith(NEW_RECORD_PREFIX)
}

function sameRecord(a, b) {
  return a.type === b.type && a.id === b.id
}

function typeFilter(type) {
  return (record) => record.type === type
}

function editProfileAndHouseholdReducer(state, action) {
  switch (action.type) {
    case "DELETE_CUSTOM_FIELDS_FIELD_DATA":
      return state.map((record) => {
        if (sameRecord(record, action.customField)) {
          const newFieldData = record.relationships?.field_data?.data.filter(
            (fd) => fd.id !== action.fieldData.id,
          )
          return {
            ...record,
            relationships: {
              ...record.relationships,
              field_data: {
                data: newFieldData,
              },
            },
          }
        } else {
          return record
        }
      })

    case "UPDATE_CUSTOM_FIELDS_FIELD_DATA_ATTRIBUTES":
      return state.map((record) => {
        if (sameRecord(record, action.customField)) {
          const unaffectedFieldData =
            record.relationships?.field_data?.data.filter((fieldDatum) => {
              return fieldDatum.id !== action.fieldData.id
            })
          return {
            ...record,
            relationships: {
              ...record.relationships,
              field_data: {
                data: [
                  ...unaffectedFieldData,
                  {
                    ...action.fieldData,
                    attributes: {
                      ...action.fieldData?.attributes,
                      ...action.attributes,
                    },
                  },
                ],
              },
            },
          }
        } else {
          return record
        }
      })
    case "UPDATE_ATTRIBUTES":
      return state.map((record) => {
        if (sameRecord(record, action.record)) {
          return {
            ...record,
            attributes: { ...record.attributes, ...action.attributes },
          }
        } else {
          return record
        }
      })
    case "RESET_STATE":
      return action.data
    case "NEW_RECORD":
      return [...state, action.data]
    case "MARK_FOR_DELETE": {
      if (isNewRecord(action.record))
        return state.filter((record) => record !== action.record)
      return state.map((record) => {
        if (sameRecord(action.record, record)) {
          return { ...record, deleting: true }
        } else {
          return record
        }
      })
    }
    case "UNMARK_FOR_DELETE":
      return state.map((record) => {
        if (sameRecord(record, action.record)) {
          const { deleting, ...nextRecord } = record
          return nextRecord
        } else {
          return record
        }
      })
    case "MARK_PRIMARY":
      return state.map((record) => {
        if (record.type === action.record.type) {
          const isPrimary = sameRecord(record, action.record)
          return {
            ...record,
            attributes: { ...record.attributes, ...{ primary: isPrimary } },
          }
        } else {
          return record
        }
      })
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}

const apiDataPropType = shape({
  attributes: object.isRequired,
  id: string.isRequired,
  type: string.isRequired,
})

const editableOptionsCollection = shape({
  campusOptions: arrayOf(apiDataPropType).isRequired,
  maritalStatusOptions: arrayOf(apiDataPropType).isRequired,
  membershipOptions: arrayOf(apiDataPropType).isRequired,
  schoolOptions: arrayOf(apiDataPropType).isRequired,
})

EditProfileAndHousehold.propTypes = {
  addressCountries: array,
  allOtherParents: bool.isRequired,
  canEditContactInfo: bool.isRequired,
  editableOptions: editableOptionsCollection.isRequired,
  householdName: string,
  isChild: bool.isRequired,
  onDiscard: func.isRequired,
  onHouseholdRoleChange: func.isRequired,
  hasOtherHouseholdMembers: bool.isRequired,
  onSave: func.isRequired,
  personId: string.isRequired,
  records: arrayOf(apiDataPropType).isRequired,
}

export function EditProfileAndHousehold({
  allOtherParents,
  canEditContactInfo,
  editableOptions,
  hasOtherHouseholdMembers,
  householdName,
  isChild,
  onDiscard,
  onHouseholdRoleChange,
  onSave,
  personId,
  records,
  addressCountries = [],
}) {
  const {
    data: { id: currentPersonId },
  } = useSession(true)
  const [state, dispatch] = useReducer(editProfileAndHouseholdReducer, [
    ...records,
  ])

  useEffect(() => {
    dispatch({
      type: "RESET_STATE",
      data: records,
    })
  }, [records])

  const parentsGuardiansEnabled = useFlipperFeatureEnabled(
    "ROLLOUT_parents_and_guardians",
  )
  const [saving, setSaving] = useState(false)
  const [fileAttachments, setFileAttachments] = useState([])
  const newRecordIdentifier = useRef(0)
  const { layout } = useContext(ServerRenderedProps)
  const { organization_contact_email: email } = layout
  const {
    currentOrganization: {
      attributes: { country_code },
    },
  } = useContext(WebBootContext)

  const [searchParams] = useSearchParams()
  const section = searchParams.get("section")
  const group = searchParams.get("group")
  const showPersonalInfo = !section || section === "personal-information"
  const showContactInfo = !section || section === "contact-information"
  const showCustomFields = !section || section === "custom-fields"
  const canEditChildAdult = currentPersonId !== personId

  function generateNewRecordIdentifier() {
    return `${NEW_RECORD_PREFIX}${newRecordIdentifier.current++}`
  }

  const generateNewRecord = (customField, attributes) => ({
    type: "FieldData",
    id: generateNewRecordIdentifier(),
    attributes,
    links: {
      self: `/people/v2/people/${personId}/church_center_custom_fields/${customField.id}/field_data`,
    },
  })

  MaritalStatusSelect.propTypes = {
    additionalOptions: arrayOf(apiDataPropType),
    onChange: func.isRequired,
    value: string.isRequired,
  }

  function MaritalStatusSelect({ value, onChange, additionalOptions }) {
    return (
      <>
        <label htmlFor="marital_status">Marital status</label>
        <select
          className="select"
          id="marital_status"
          name="marital_status"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option key="0" value="">
            Select...
          </option>
          {additionalOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.attributes.value}
            </option>
          ))}
        </select>
      </>
    )
  }

  PrimarySelect.propTypes = {
    additionalOptions: array,
    disabled: bool,
    id: string.isRequired,
    label: string.isRequired,
    onChange: func.isRequired,
    options: array,
    value: string.isRequired,
  }

  function PrimarySelect({
    id,
    label,
    value,
    onChange,
    disabled = false,
    options = [],
  }) {
    id = `primary_select_${id}`

    return (
      <div className="d-f fd-c g-1">
        <label className="f-1  pb-0 mb-0@sm" htmlFor={id}>
          <span className="d-b">Primary {label}</span>
        </label>
        <select
          id={id}
          disabled={disabled}
          className="select"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onChange(e.target.value)}
        >
          {options.map((option) => (
            <option value={option} key={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    )
  }

  function deleteCustomFieldRecord({ customField, fieldData }) {
    dispatch({
      type: "DELETE_CUSTOM_FIELDS_FIELD_DATA",
      customField,
      fieldData,
    })
  }

  function updateCustomFieldsAttributes({
    customField,
    record,
    attributes,
    file,
    hasRemovedFile = false,
  }) {
    if (file || hasRemovedFile) {
      setFileAttachments((prev) => {
        if (hasRemovedFile) {
          return prev.filter((att) => att.customFieldId !== customField.id)
        }

        const existingAttachment = prev.find(
          (att) => att.customFieldId === customField.id,
        )

        const files = existingAttachment?.files || []
        const newFiles = [...files, file]

        return [
          ...prev.filter((att) => att.customFieldId !== customField.id),
          {
            customFieldId: customField.id,
            form_field_id: customField.id,
            fieldDataId: record?.id,
            files: newFiles,
          },
        ]
      })
    }
    dispatch({
      type: "UPDATE_CUSTOM_FIELDS_FIELD_DATA_ATTRIBUTES",
      fieldData: record || generateNewRecord(customField, attributes),
      customField,
      attributes,
    })
  }

  function updateAttribute(record, attribute, value) {
    dispatch({
      type: "UPDATE_ATTRIBUTES",
      record,
      attributes: { [attribute]: value },
    })
  }

  function updateAttributes(record, attributes) {
    dispatch({
      type: "UPDATE_ATTRIBUTES",
      record,
      attributes,
    })
  }

  function createRecord(record) {
    const url = record.links.self
    const pathname = url.replace(buildSubdomain("api"), "")
    const { type, attributes } = record
    return sessionApiClient.post(pathname, { data: { type, attributes } })
  }

  function deleteRecord(record) {
    const url = record.links.self
    const pathname = url.replace(buildSubdomain("api"), "")
    return sessionApiClient.del(pathname)
  }

  function deleteCustomFieldData(fieldData) {
    const url = fieldData.links.self
    const pathname = url.replace(buildSubdomain("api"), "")

    return sessionApiClient.del(pathname)
  }

  function updateCustomFieldData(fieldData) {
    const { file, file_content_type, file_name, file_size, ...changeSet } = {
      ...fieldData.attributes,
    }

    const url = fieldData.links.self
    const pathname = url.replace(buildSubdomain("api"), "")

    if (isNewRecord(fieldData)) {
      return sessionApiClient.post(pathname, {
        data: {
          type: "FieldData",
          attributes: changeSet,
        },
      })
    } else {
      return sessionApiClient.patch(pathname, {
        data: {
          attributes: changeSet,
        },
      })
    }
  }

  function updateRecord(record) {
    const nextAttributes = record.attributes
    const prevAttributes = records.find((r) => sameRecord(r, record)).attributes

    let changeSet = Object.keys(nextAttributes).reduce(
      (changes, key) =>
        nextAttributes[key] !== prevAttributes[key]
          ? { ...changes, [key]: nextAttributes[key] }
          : changes,
      {},
    )

    if (record.deleteValueAttributeForPatch) delete changeSet.value

    if (record.type === "SchoolOption") {
      changeSet = { school_id: changeSet.school_id }
    } else if (record.type === "Campus") {
      changeSet = { primary_campus_id: record.attributes.primary_campus_id }
    }

    const url = record.links.self
    const pathname = url.replace(buildSubdomain("api"), "")
    return sessionApiClient.patch(pathname, {
      data: { attributes: changeSet },
    })
  }
  async function uploadFiles() {
    if (!fileAttachments.length) return []

    try {
      const uploads = fileAttachments.map((attachment) =>
        uploadFileAttachment({
          form_field_id: attachment.form_field_id,
          files: attachment.files,
        }),
      )

      const uploadedAttachments = await Promise.all(uploads)
      uploadedAttachments.forEach((result) => {
        const attachment = fileAttachments.find(
          (att) => att.form_field_id === result.form_field_id,
        )

        if (result.files?.[0] && attachment) {
          const customField = state.find(
            (record) =>
              record.type === "ChurchCenterCustomField" &&
              record.id === attachment.customFieldId,
          )

          if (customField) {
            // Find the existing field data record for this file
            // It should already exist from the initial state update with the file name
            const existingFieldData =
              customField.relationships?.field_data?.data.find(
                (fd) =>
                  fd.id === attachment.fieldDataId ||
                  fd.attributes?.value === attachment.files[0].name,
              )

            if (existingFieldData) {
              dispatch({
                type: "UPDATE_CUSTOM_FIELDS_FIELD_DATA_ATTRIBUTES",
                fieldData: existingFieldData,
                customField,
                attributes: {
                  value: result.files[0].fileID,
                },
              })
            } else {
              console.warn(
                "Could not find existing field data record for file",
                attachment.files[0].name,
              )
              dispatch({
                type: "UPDATE_CUSTOM_FIELDS_FIELD_DATA_ATTRIBUTES",
                fieldData: { id: attachment.fieldDataId },
                customField,
                attributes: {
                  value: result.files[0].fileID,
                },
              })
            }
          }
        }
      })

      setFileAttachments([])

      return uploadedAttachments
    } catch (error) {
      console.error("Error uploading files:", error)
      return []
    }
  }

  async function saveChanges() {
    setSaving(true)

    try {
      let uploadedFiles = []
      if (fileAttachments.length > 0) {
        const uploadedAttachments = await uploadFiles()

        uploadedFiles = uploadedAttachments.flatMap((result) =>
          result.files.map((file) => ({
            customFieldId: fileAttachments.find(
              (att) => att.form_field_id === result.form_field_id,
            )?.customFieldId,
            originalFileName: fileAttachments.find(
              (att) => att.form_field_id === result.form_field_id,
            )?.files[0]?.name,
            fileID: file.fileID,
          })),
        )
        await new Promise((resolve) => {
          setTimeout(() => {
            resolve()
          }, 100)
        })
      }

      const {
        customFieldsToCreate,
        customFieldsToUpdate,
        customFieldsToDelete,
      } = groupCustomFieldRecordsForSaving(state, records, uploadedFiles)

      const deletingCustomFieldData = customFieldsToDelete.map(
        deleteCustomFieldData,
      )
      const creatingCustomFieldData = customFieldsToCreate.map(
        updateCustomFieldData,
      )
      const updatingCustomFieldData = customFieldsToUpdate.map(
        updateCustomFieldData,
      )

      const recordsToCreate = state.filter(isNewRecord)
      const recordsToDelete = state.filter((record) => record.deleting)
      const recordsToUpdate = state.filter((recordInState) => {
        if (recordsToCreate.includes(recordInState)) return false
        if (recordsToDelete.includes(recordInState)) return false
        return records.some(
          (recordInProps) =>
            sameRecord(recordInState, recordInProps) &&
            recordInProps.attributes !== recordInState.attributes,
        )
      })

      const creating = recordsToCreate.map(createRecord)
      const deleting = recordsToDelete.map(deleteRecord)
      const updating = recordsToUpdate.map(updateRecord)

      await Promise.all([
        ...creating,
        ...deleting,
        ...updating,
        ...creatingCustomFieldData,
        ...updatingCustomFieldData,
        ...deletingCustomFieldData,
      ])

      onSave()
    } catch (error) {
      console.error("Error saving changes:", error)
    } finally {
      setSaving(false)
    }
  }

  function addEmail() {
    dispatch({
      type: "NEW_RECORD",
      data: {
        id: generateNewRecordIdentifier(),
        type: "Email",
        attributes: {
          location: "Home",
          address: "",
        },
        links: {
          self: `/people/v2/people/${personId}/emails`,
        },
      },
    })
  }

  function addPhoneNumber() {
    dispatch({
      type: "NEW_RECORD",
      data: {
        id: generateNewRecordIdentifier(),
        type: "PhoneNumber",
        attributes: {
          location: "Mobile",
          number: "",
        },
        links: {
          self: `/people/v2/people/${personId}/phone_numbers`,
        },
      },
    })
  }

  function addAddress() {
    dispatch({
      type: "NEW_RECORD",
      data: {
        id: generateNewRecordIdentifier(),
        type: "Address",
        attributes: {
          location: "Home",
          street: "",
          city: "",
          state: "",
          zip: "",
          country_code,
        },
        links: {
          self: `/people/v2/people/${personId}/addresses`,
        },
      },
    })
  }

  function markPrimary(record) {
    dispatch({ type: "MARK_PRIMARY", record })
  }

  function unmarkForDelete(record) {
    dispatch({ type: "UNMARK_FOR_DELETE", record })
  }

  function markForDelete(record) {
    dispatch({ type: "MARK_FOR_DELETE", record })
  }

  function domId(record, suffix = "") {
    return `${record.type}_${record.id}_${suffix}`
  }

  const getPrimaryRecord = (list) =>
    list.find(
      (record) =>
        record.attributes.primary === true && record.deleting !== true,
    ) || list.find((record) => !record.deleting)

  const addresses = state.filter(typeFilter("Address"))
  const anniversary = state.find(typeFilter("Anniversary"))
  const birthdate = state.find(typeFilter("Birthdate"))
  const child = state.find(typeFilter("Child"))
  const emails = state.filter(typeFilter("Email"))
  const gender = state.find(typeFilter("Gender"))
  const grade = state.find(typeFilter("Grade"))
  const graduationYear = state.find(typeFilter("Graduation_Year"))
  const maritalStatus = state.find(typeFilter("MaritalStatus"))
  const medicalNotes = state.find(typeFilter("Medical_Notes"))
  const phoneNumbers = state.filter(typeFilter("PhoneNumber"))
  const school = state.find(typeFilter("SchoolOption"))
  const campus = state.find(typeFilter("Campus"))
  const membership = state.find(typeFilter("Membership"))
  const householdRole = state.find(typeFilter("HouseholdMember"))

  const {
    campusOptions,
    maritalStatusOptions,
    membershipOptions,
    schoolOptions,
  } = editableOptions
  const customFields = state.filter(typeFilter("ChurchCenterCustomField")) // array of custom fields
  const groupedCustomFields = groupCustomFieldsByHeaders(customFields)
  const customFieldsToRender = group
    ? [groupedCustomFields[group]]
    : groupedCustomFields

  const birthdayRef = useRef(birthdate.attributes.birthdate)
  const {
    birthdate: birthdateFieldRule,
    campus: campusEditingRule,
    membership_type: membershipEditingRule,
  } = useBuiltinFieldRules()
  const birthdayDisabled =
    birthdateFieldRule === "view" ||
    (birthdateFieldRule === "add_if_blank" && birthdayRef.current !== null)

  const primaryEmail = getPrimaryRecord(emails)?.attributes.address || ""
  const primaryPhoneNumber =
    getPrimaryRecord(phoneNumbers)?.attributes.number || ""

  function CampusAndMembership() {
    if (campusEditingRule === "hidden" && membershipEditingRule === "hidden")
      return null

    return (
      <div className="d-f@sm f_1 g-2 g-4@sm">
        {campusEditingRule !== "hidden" && (
          <div>
            <CampusSelect
              campuses={campusOptions}
              onChange={(id) => {
                updateAttribute(campus, "primary_campus_id", id)
              }}
              value={campus.attributes.primary_campus_id}
            />
          </div>
        )}
        {membershipEditingRule !== "hidden" && (
          <div>
            <MembershipSelect
              onChange={(id) => {
                const newMembership = membershipOptions.find(
                  (mo) => mo.id === id,
                )
                updateAttributes(membership, {
                  membership_status_id: id,
                  value: newMembership?.attributes?.value || "",
                })
              }}
              memberships={membershipOptions}
              value={membership.attributes.membership_status_id}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        saveChanges()
      }}
      className="d-f fd-c g-4"
    >
      {showPersonalInfo && (
        <div>
          <Heading level={2} size={3} text="Personal information" />
          {!canEditContactInfo && (
            <div style={{ backgroundColor: colors.tint8, padding: "10px" }}>
              <Icon symbol="general#exclamation-triangle" />{" "}
              <span>
                This person has administrative rights in Planning Center.
                Because of this, only they can edit their contact info.
              </span>
            </div>
          )}

          <div className="action-drawer d-f fd-c g-2 mt-2">
            <CampusAndMembership />
            <div className="d-f fd-c fd-r@sm g-2 g-4@sm f_1">
              <div>
                <GenderSelect
                  label="Gender"
                  onChange={(value) => updateAttribute(gender, "gender", value)}
                  value={gender.attributes.gender || ""}
                />
              </div>
              <div>
                <DateSelector
                  disabled={birthdayDisabled}
                  initialValue={birthdate.attributes.birthdate}
                  label={
                    <div className="label pb-0">
                      Birthdate
                      {birthdayDisabled && (
                        <Icon symbol="general#lock" className="fs-2 pl-1" />
                      )}
                    </div>
                  }
                  name="birthdate"
                  onChange={(newBirthdate) =>
                    updateAttribute(birthdate, "birthdate", newBirthdate)
                  }
                />
              </div>
            </div>
            <GradeAndSchool
              {...{
                grade,
                graduationYear,
                isChild,
                school,
                schoolOptions,
                updateAttribute,
                updateAttributes,
              }}
            />
            {!isChild && (
              <div className="d-f fd-c g-2 g-4@sm fd-r@sm f_1">
                <div>
                  <MaritalStatusSelect
                    additionalOptions={maritalStatusOptions}
                    onChange={(id) => {
                      const value = maritalStatusOptions.find(
                        (option) => option.id === id,
                      )?.attributes?.value
                      updateAttributes(maritalStatus, {
                        marital_status_id: id,
                        value,
                      })
                    }}
                    value={maritalStatus.attributes.marital_status_id}
                  />
                </div>
                {maritalStatus.attributes.value === "Married" && (
                  <div>
                    <DateSelector
                      initialValue={anniversary.attributes.anniversary}
                      label="Anniversary"
                      name="anniversary"
                      onChange={(newAnniversary) =>
                        updateAttribute(
                          anniversary,
                          "anniversary",
                          newAnniversary,
                        )
                      }
                    />
                  </div>
                )}
              </div>
            )}
            <HouseholdRoleSection
              allOtherParents={allOtherParents}
              canEditChildAdult={canEditChildAdult}
              child={child}
              hasOtherHouseholdMembers={hasOtherHouseholdMembers}
              householdName={householdName}
              householdRole={householdRole}
              onHouseholdRoleChange={onHouseholdRoleChange}
              parentsGuardiansEnabled={parentsGuardiansEnabled}
              updateAttributes={updateAttributes}
            />
            <div>
              <label htmlFor="medical_notes">Medical notes</label>
              <textarea
                id="medical_notes"
                className="text-input"
                placeholder="Please use a comma-separated list for brevity. Ex: Pollen, nuts, and dairy."
                onChange={(e) =>
                  updateAttribute(medicalNotes, "medical_notes", e.target.value)
                }
                value={medicalNotes.attributes.medical_notes}
              />
            </div>
          </div>
          <div className="d-f mt-1 fs-4 c-tint3">
            <span className="p-r t-2p">
              <Icon symbol="general#lock" className="mr-4p" />
            </span>
            <p>
              Locked fields can only be edited by a church administrator.{" "}
              <a href={`mailto:${email}`}>Contact us</a> for help.
            </p>
          </div>
        </div>
      )}
      {showContactInfo && (
        <div className="d-f fd-c g-2">
          <Heading level={2} size={3} text="Contact information" />
          {!canEditContactInfo && (
            <div style={{ backgroundColor: colors.tint8, padding: "10px" }}>
              <Icon symbol="general#exclamation-triangle" />{" "}
              <span>
                This person has administrative rights in Planning Center.
                Because of this, only they can edit their contact info.
              </span>
            </div>
          )}
          <div className="action-drawer d-f fd-c g-2">
            <div className="d-f fd-c g-1">
              <div className="d-f ai-c">
                <span className="c-tint3 p-r t-1p mr-1">
                  <Icon symbol="general#outlined-envelope" />
                </span>
                <Heading level={3} size={4} text="Email" />
              </div>
              <div className="d-f fd-c g-2">
                {emails.map((email) => (
                  <div key={email.id}>
                    {email.deleting ? (
                      <Fragment>
                        <div className="c-tint3 fs-5">
                          {email.attributes.location}
                        </div>
                        <div>
                          <strike
                            aria-label={`email address to be deleted: ${email.attributes.address}`}
                          >
                            {email.attributes.address}
                          </strike>
                          <button
                            type="button"
                            disabled={saving}
                            className="fw-500 ml-1 c-topaz stripped-btn p-0"
                            aria-label={`restore email address: ${email.attributes.address}`}
                            onClick={() => unmarkForDelete(email)}
                          >
                            Restore
                          </button>
                        </div>
                      </Fragment>
                    ) : (
                      <div className="d-f fd-c g-4p">
                        <div className="d-f@sm ai-c">
                          <LocationSelect
                            disabled={!canEditContactInfo || saving}
                            id={domId(email)}
                            value={email.attributes.location}
                            onChange={(value) =>
                              updateAttribute(email, "location", value)
                            }
                          />
                          <div className="f-1 pl-1@sm">
                            <label htmlFor={domId(email, "address")}>
                              <span className="screen-reader-text">Email</span>
                              <input
                                id={domId(email, "address")}
                                autoFocus={isNewRecord(email)}
                                disabled={!canEditContactInfo || saving}
                                inputMode="email"
                                onChange={(e) =>
                                  updateAttribute(
                                    email,
                                    "address",
                                    e.target.value,
                                  )
                                }
                                pattern="^\S+@\S+\.\S+$"
                                placeholder="name@email.com"
                                type="text"
                                value={email.attributes.address}
                              />
                            </label>
                          </div>
                        </div>
                        <div className="d-f ai-fb jc-fe">
                          <button
                            type="button"
                            disabled={!canEditContactInfo || saving}
                            className="destroy-btn minor-compact-btn btn"
                            aria-label={`mark email for deletion: ${email.attributes.address}`}
                            onClick={() => markForDelete(email)}
                          >
                            <span className="mr-4p fs-6" aria-hidden="true">
                              <Icon symbol="general#x" />
                            </span>
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="ta-r">
              <button
                disabled={!canEditContactInfo || saving}
                className="btn secondary-btn minor-btn"
                type="button"
                onClick={addEmail}
              >
                <span
                  className="mr-4p"
                  style={{ fontSize: "9px" }}
                  aria-hidden="true"
                >
                  <Icon symbol="general#plus" />
                </span>
                Add email address
              </button>
            </div>
            {emails.filter((address) => !address.deleting).length > 0 && (
              <PrimarySelect
                id="email"
                disabled={!canEditContactInfo || saving}
                label="Email"
                value={primaryEmail}
                options={emails.map((email) => email.attributes.address)}
                onChange={(value) =>
                  markPrimary(
                    emails.find((email) => email.attributes.address === value),
                  )
                }
              />
            )}
          </div>
          <div className="action-drawer d-f fd-c g-2">
            <div className="d-f fd-c g-1">
              <div className="d-f ai-c">
                <span className="c-tint3 p-r t-1p mr-1">
                  <Icon symbol="general#mobile-phone" />
                </span>
                <Heading level={3} size={4} text="Phone" />
              </div>
              <div className="d-f fd-c g-2">
                {phoneNumbers.map((phoneNumber) => (
                  <div css={styles.editingFieldContainer} key={phoneNumber.id}>
                    {phoneNumber.deleting ? (
                      <Fragment>
                        <div className="c-tint3 fs-5">
                          {phoneNumber.attributes.location}
                        </div>
                        <div>
                          <strike
                            aria-label={`phone number to be deleted: ${phoneNumber.attributes.number}`}
                          >
                            {phoneNumber.attributes.number}
                          </strike>
                          <button
                            type="button"
                            disabled={!canEditContactInfo || saving}
                            className="fw-500 ml-1 c-topaz stripped-btn p-0"
                            aria-label={`restore phone number: ${phoneNumber.attributes.number}`}
                            onClick={() => unmarkForDelete(phoneNumber)}
                          >
                            Restore
                          </button>
                        </div>
                      </Fragment>
                    ) : (
                      <Fragment>
                        <div className="d-f@sm ai-c">
                          <LocationSelect
                            disabled={!canEditContactInfo || saving}
                            id={domId(phoneNumber)}
                            value={phoneNumber.attributes.location}
                            additionalOptions={[
                              phoneNumber.attributes.location !== "Mobile" &&
                                "Mobile",
                              phoneNumber.attributes.location,
                            ].filter(Boolean)}
                            onChange={(value) =>
                              updateAttribute(phoneNumber, "location", value)
                            }
                          />
                          <div className="f-1 pl-1@sm">
                            <label htmlFor={domId(phoneNumber, "number")}>
                              <span className="screen-reader-text">
                                Phone number
                              </span>
                              <input
                                id={domId(phoneNumber, "number")}
                                autoFocus={isNewRecord(phoneNumber)}
                                disabled={!canEditContactInfo || saving}
                                inputMode="number"
                                onChange={(e) =>
                                  updateAttribute(
                                    phoneNumber,
                                    "number",
                                    e.target.value,
                                  )
                                }
                                placeholder="888-555-1212"
                                required
                                type="text"
                                value={phoneNumber.attributes.number}
                              />
                            </label>
                          </div>
                        </div>
                        <div className="d-f ai-fb jc-fe">
                          <button
                            type="button"
                            disabled={!canEditContactInfo || saving}
                            className="destroy-btn minor-compact-btn btn"
                            aria-label={`mark phone number for deletion: ${phoneNumber.attributes.number}`}
                            onClick={() => markForDelete(phoneNumber)}
                          >
                            <span className="mr-4p fs-6" aria-hidden="true">
                              <Icon symbol="general#x" />
                            </span>
                            Delete
                          </button>
                        </div>
                      </Fragment>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="ta-r">
              <button
                className="btn secondary-btn minor-btn"
                disabled={!canEditContactInfo || saving}
                type="button"
                onClick={addPhoneNumber}
              >
                <span
                  className="mr-4p"
                  style={{ fontSize: "9px" }}
                  aria-hidden="true"
                >
                  <Icon symbol="general#plus" />
                </span>
                Add phone number
              </button>
            </div>
            {phoneNumbers.filter((number) => !number.deleting).length > 0 && (
              <PrimarySelect
                disabled={!canEditContactInfo || saving}
                id="phone_numbers"
                label="Phone Number"
                value={primaryPhoneNumber}
                options={phoneNumbers.map((number) => number.attributes.number)}
                onChange={(value) =>
                  markPrimary(
                    phoneNumbers.find(
                      (number) => number.attributes.number === value,
                    ),
                  )
                }
              />
            )}
          </div>
          <div className="action-drawer d-f fd-c g-2">
            <div className="d-f fd-c g-1">
              <div className="d-f ai-c">
                <span className="c-tint3 p-r t-1p mr-4p">
                  <Icon symbol="general#outlined-location-pin" />
                </span>
                <Heading level={3} size={4} text="Address" />
              </div>
              <div className="d-f fd-c g-2">
                {addresses.map((address) => (
                  <Address
                    key={address.id}
                    address={address}
                    canEditContactInfo={canEditContactInfo}
                    isNewRecord={isNewRecord}
                    saving={saving}
                    markForDelete={markForDelete}
                    unmarkForDelete={unmarkForDelete}
                    updateAttribute={updateAttribute}
                    domId={domId}
                    initialCountry={addressCountries.find(
                      (country) =>
                        country.id === address.attributes.country_code,
                    )}
                  />
                ))}
              </div>
            </div>
            <div className="ta-r">
              <button
                className="btn secondary-btn minor-btn"
                disabled={!canEditContactInfo || saving}
                type="button"
                onClick={addAddress}
              >
                <span
                  className="mr-4p"
                  style={{ fontSize: "9px" }}
                  aria-hidden="true"
                >
                  <Icon symbol="general#plus" />
                </span>
                Add mailing address
              </button>
            </div>
          </div>
        </div>
      )}

      {showCustomFields &&
        customFields.length > 0 &&
        customFieldsToRender.map((group, index) => (
          <div className="d-f fd-c g-2" key={"custom-fields-" + index}>
            <Heading level={2} size={3} text={group.title} />
            <CustomFieldsForm
              key={group.title}
              customFields={group.items}
              updateAttributes={updateCustomFieldsAttributes}
              deleteFieldDataRecord={deleteCustomFieldRecord}
            />
          </div>
        ))}

      <div className="d-f" css={styles.wrapper}>
        <div className="d-f g-2 jc-fe f-1" css={styles.innerWrapper}>
          <button
            type="button"
            onClick={() => onDiscard()}
            className="c-tint3 text-btn"
            disabled={saving}
          >
            Discard changes
          </button>
          <button type="submit" className="btn" disabled={saving}>
            Save changes
          </button>
        </div>
      </div>
    </form>
  )
}

const styles = {
  wrapper: css`
    justify-content: space-between;
    text-align: "center";

    @media (max-width: 600px) {
      align-items: center;
      flex-direction: column-reverse;
    }
  `,

  innerWrapper: css`
    @media (max-width: 600px) {
      flex-direction: column-reverse;
      margin-bottom: 15px;
    }
  `,
}
